import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { Rhino3dmLoader } from "three/examples/jsm/loaders/3DMLoader.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { GTAOPass } from "three/examples/jsm/postprocessing/GTAOPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { Loader2, Check, AlertTriangle, UploadCloud, Camera } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const RHINO_LIB = "https://cdn.jsdelivr.net/npm/rhino3dm@8.17.0/";

// 2026-02-28 spec — every visual constant (colour, material, lighting,
// AO, resolution, camera list) comes from the admin panel via
// `/api/config/render-preset`. Sellers cannot override any of it.
// The values below are ONLY used as an emergency fallback if the
// settings endpoint is unreachable at render time — they mirror the
// server-side ship-defaults in `RenderConfig`.
const FALLBACK_PRESET = {
  object_color: "#c8ccd0",
  background_color: "#0f0f10",
  material_metallic: 0.05,
  material_roughness: 0.805,        // = 1 - 0.30 * 0.65 (matches server default)
  material_specular: 0.30,
  material_shininess: 20,
  light_intensity: 0.75,
  ao_strength: 0.90,
  output_size: 1600,
  framing: 0.85,
  shadow_opacity: 0,
  view_angles: [
    { name: "front",           elev_deg:  0,   azim_deg:   0 },
    { name: "back",            elev_deg:  0,   azim_deg: 180 },
    { name: "left",            elev_deg:  0,   azim_deg:  90 },
    { name: "right",           elev_deg:  0,   azim_deg: -90 },
    { name: "top",             elev_deg: 89,   azim_deg:   0 },
    { name: "bottom",          elev_deg: -89,  azim_deg:   0 },
    { name: "three_quarter_a", elev_deg: 30,   azim_deg:  45 },
    { name: "three_quarter_b", elev_deg: 30,   azim_deg: -45 },
  ],
};

const hexToRgbNormalized = (h) => {
  const v = String(h || "#000000").replace("#", "");
  return {
    r: parseInt(v.slice(0, 2), 16) / 255,
    g: parseInt(v.slice(2, 4), 16) / 255,
    b: parseInt(v.slice(4, 6), 16) / 255,
  };
};

// Quick WebGL support probe. Returns { ok, reason, webglVersion, maxTexture,
// renderer, vendor, isSoftware }. We now REQUIRE WebGL2 (WebGL1 is
// end-of-life on modern browsers and its shader precision fluctuates),
// and we sniff WEBGL_debug_renderer_info so software rasterisers
// (SwiftShader / llvmpipe / ANGLE-on-D3D-warp) route to manual upload
// instead of grinding for 5 minutes and producing a milky render.
// The probe now also disposes its context via `forceContextLoss` in a
// `finally` block so the tiny throwaway WebGL context doesn't linger
// on the browser's context budget (most desktop browsers cap at 16).
function detectWebGL() {
  let canvas = null;
  let probe = null;
  try {
    canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2");
    if (!gl) {
      // Grab a WebGL1 context ONLY to sniff the renderer string for a
      // useful error, then bail — we don't try to render on WebGL1.
      const gl1 = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
      let extra = "";
      if (gl1) {
        const dbg = gl1.getExtension("WEBGL_debug_renderer_info");
        if (dbg) extra = ` (${gl1.getParameter(dbg.UNMASKED_RENDERER_WEBGL)})`;
      }
      return { ok: false, reason: `Your browser only supports WebGL1${extra}; we need WebGL2 for browser rendering.` };
    }
    const maxTexture = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 0;
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : "";
    const vendor = dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : "";
    const rlow = String(renderer || "").toLowerCase();
    const isSoftware = /swiftshader|software|llvmpipe|angle.*(software|d3d.*warp)|microsoft.*basic/.test(rlow);
    if (isSoftware) {
      return {
        ok: false, reason: `Your browser is using a software renderer (${renderer}). Please enable hardware acceleration or upload your own photos.`,
        webglVersion: 2, maxTexture, renderer, vendor, isSoftware: true,
      };
    }
    // Real allocation check — some environments hand back a context but bail at first buffer alloc.
    probe = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false, powerPreference: "high-performance" });
    return { ok: true, reason: "", webglVersion: 2, maxTexture, renderer, vendor, isSoftware: false };
  } catch (e) {
    return { ok: false, reason: `WebGL initialisation failed: ${e.message || e}` };
  } finally {
    try {
      if (probe) {
        const ext = probe.getContext()?.getExtension?.("WEBGL_lose_context");
        ext?.loseContext?.();
        probe.dispose();
        probe.forceContextLoss?.();
      }
    } catch (_) { /* ignore */ }
  }
}

// Post a lightweight telemetry beacon. Fire-and-forget — never blocks.
function sendTelemetry(payload) {
  try {
    fetch(`${window.__jx_apiRoot || ""}/api/render/telemetry`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch (_) { /* ignore */ }
}

/**
 * ClientStudioRenderer
 * --------------------
 * Loads the seller's STL/3DM in the browser, renders 8 studio camera
 * angles offscreen at RENDER_SIZE^2 with a matte-silver PBR material +
 * GTAO ambient occlusion, and uploads each JPEG through the standard
 * `/seller/listings/{id}/photos/upload?source=client_render` endpoint.
 *
 * Props:
 *   listingId   — string, required. The listing that was just created.
 *   hasStl      — boolean. True if the listing has an .stl available.
 *   hasDm       — boolean. True if the listing has a .3dm available.
 *                 If both are present, STL is preferred (faster in-browser parse).
 *   onComplete  — (photosCount) => void. Called after every view has landed on the server.
 *   onFailure   — (reason) => void. Called on WebGL absence, mesh load / render error, or OOM.
 *   onProgress  — (viewIndex, viewName) => void. Called after each view uploads successfully.
 *
 * Bytes are fetched through the `api` client so the seller's JWT rides
 * along — the render-source endpoint refuses non-owners.
 */
export default function ClientStudioRenderer({
  listingId, hasStl, hasDm, stlUrl: _legacyStlUrl, dmUrl: _legacyDmUrl,
  onComplete, onFailure, onProgress, maxViews,
}) {
  const [phase, setPhase] = useState("preparing"); // preparing | rendering | uploading | done | failed
  const [progress, setProgress] = useState(0);     // views completed
  const [errorMsg, setErrorMsg] = useState("");
  // Blob URLs of the finished JPEG for each view — displayed as
  // thumbnails in the tile grid so sellers see the ACTUAL rendered
  // photo, not a placeholder checkmark box.
  const [thumbs, setThumbs] = useState([]);
  const cancelled = useRef(false);

  useEffect(() => {
    // Rerun ONLY on listingId change; deliberate. Regenerate flow uses a fresh mount.
    cancelled.current = false;
    // 2026-02-28 — auto-retry once on first failure. First-mount races
    // (auth token not yet on the api client, config endpoint slow to
    // warm) previously forced sellers to click Regenerate a second
    // time; a silent 1.5-s retry now handles those transient failures
    // without any UI blip.
    // 2026-03-01 — Retry cap. `attempted` becomes true after the
    // first failure so a second consecutive failure lands in the
    // manual-upload branch instead of infinitely burning quota +
    // creating duplicate photo uploads on the server.
    let attempted = false;
    const kick = () => runPipeline().catch(async (err) => {
      if (cancelled.current) return;
      if (!attempted) {
        attempted = true;
        // eslint-disable-next-line no-console
        console.info("[ClientStudioRenderer] first render errored, retrying once:", err?.message);
        setPhase("preparing"); setProgress(0); setThumbs([]);
        await new Promise((r) => setTimeout(r, 1500));
        if (cancelled.current) return;
        return kick();
      }
      const msg = err?.message || String(err);
      setErrorMsg(msg);
      setPhase("failed");
      onFailure?.(msg);
    });
    kick();
    return () => { cancelled.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  async function runPipeline() {
    // ---------- WebGL support gate --------------------------------------
    const webgl = detectWebGL();
    // Snapshot the diagnostics for every telemetry event this pipeline fires.
    const gpuMeta = {
      user_agent: navigator.userAgent,
      gpu_renderer: webgl.renderer || null,
      gpu_vendor: webgl.vendor || null,
      webgl_version: webgl.webglVersion || null,
      max_texture_size: webgl.maxTexture || null,
      device_pixel_ratio: window.devicePixelRatio || 1,
    };
    if (!webgl.ok) {
      sendTelemetry({ ...gpuMeta, listing_id: listingId, event: "failure", reason: webgl.reason });
      throw new Error(webgl.reason);
    }

    // ---------- Fetch admin render preset (single source of truth) ------
    let preset = FALLBACK_PRESET;
    try {
      const { data } = await api.get("/config/render-preset");
      preset = { ...FALLBACK_PRESET, ...data };
    } catch (e) {
      console.warn("[ClientStudioRenderer] Could not fetch admin preset — using defaults:", e?.message);
    }
    let viewAngles = preset.view_angles || FALLBACK_PRESET.view_angles;
    // 2026-09 gallery cap: a listing shows at most 10 photos and the
    // seller's own uploads take priority, so only render as many studio
    // views as there are free slots. The three-quarter angles lead the
    // list because they are the views buyers judge a piece by.
    if (Number.isFinite(maxViews)) {
      if (maxViews <= 0) {
        onComplete?.({ rendered: 0, skipped: "gallery_full" });
        return;
      }
      if (maxViews < viewAngles.length) {
        const priority = ["three_quarter_a", "three_quarter_b", "front", "right", "left", "back", "top", "bottom"];
        viewAngles = [...viewAngles]
          .sort((a, b) => {
            const ia = priority.indexOf(a.name), ib = priority.indexOf(b.name);
            return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
          })
          .slice(0, maxViews);
      }
    }
    // ---------- Adaptive resolution -------------------------------------
    // Step down output size on GPUs whose MAX_TEXTURE_SIZE is small
    // (mobile / integrated). GTAOPass allocates a full-size G-buffer
    // + depth attachment; on a 4096 max-texture GPU asking for a 1600
    // AO buffer plus mipmap chain already stresses the budget.
    const adminSize = preset.output_size || 1600;
    const gpuCap = webgl.maxTexture || 8192;
    const RENDER_SIZE = Math.min(adminSize, Math.max(768, gpuCap / 4));

    // ---------- Kick off telemetry --------------------------------------
    sendTelemetry({
      ...gpuMeta, listing_id: listingId, event: "start",
      expected_views: viewAngles.length, attempt: 1,
    });

    // ---------- Load the mesh -------------------------------------------
    setPhase("preparing");
    const geometry = await loadGeometry(gpuMeta);
    if (cancelled.current) return;
    const polyCount = (geometry?.attributes?.position?.count || 0) / 3;

    // ---------- Build the renderer + scene ------------------------------
    const canvas = document.createElement("canvas");
    canvas.width = RENDER_SIZE;
    canvas.height = RENDER_SIZE;

    // Context-loss handler — mark a flag so every toBlob check knows
    // to bail early instead of writing a blank image. GTAOPass in
    // particular has been observed to trip context loss on iOS Safari
    // when it allocates its G-buffer above the device limit.
    let contextLost = false;
    const onLost = (e) => { e.preventDefault?.(); contextLost = true; };
    canvas.addEventListener("webglcontextlost", onLost, false);

    let renderer, pmrem, composer, envTex, gtaoPass, material, mesh;
    try {
      try {
        renderer = new THREE.WebGLRenderer({
          canvas, antialias: true, alpha: false,
          preserveDrawingBuffer: true, powerPreference: "high-performance",
        });
      } catch (e) {
        throw new Error(`Renderer creation failed: ${e.message || e}`);
      }
      renderer.setSize(RENDER_SIZE, RENDER_SIZE, false);
      renderer.setPixelRatio(1);
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.15;
      renderer.outputColorSpace = THREE.SRGBColorSpace;

      const scene = new THREE.Scene();
      const bg = hexToRgbNormalized(preset.background_color);
      scene.background = new THREE.Color(bg.r, bg.g, bg.b);
      pmrem = new THREE.PMREMGenerator(renderer);
      envTex = pmrem.fromScene(new RoomEnvironment(), 0.06).texture;
      scene.environment = envTex;

      const obj = hexToRgbNormalized(preset.object_color);
      material = new THREE.MeshStandardMaterial({
        color: new THREE.Color(obj.r, obj.g, obj.b),
        metalness: preset.material_metallic,
        roughness: preset.material_roughness,
      });

      geometry.computeBoundingBox();
      const box = geometry.boundingBox;
      const size = new THREE.Vector3(); box.getSize(size);
      const center = new THREE.Vector3(); box.getCenter(center);
      geometry.translate(-center.x, -center.y, -center.z);
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      const s = 1 / maxDim;
      geometry.scale(s, s, s);
      geometry.computeVertexNormals();
      mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      const li = preset.light_intensity;
      const keyLight = new THREE.DirectionalLight(0xffffff, 2.0 * li); keyLight.position.set(1.5, 2.0, 1.5); scene.add(keyLight);
      const fillLight = new THREE.DirectionalLight(0xffffff, 1.1 * li); fillLight.position.set(-1.5, 0.6, 1.2); scene.add(fillLight);
      const rimLight = new THREE.DirectionalLight(0xffffff, 0.8 * li); rimLight.position.set(0, -1.0, -2.0); scene.add(rimLight);
      scene.add(new THREE.AmbientLight(0xffffff, 0.55 * li));

      const halfFrame = preset.framing;
      const camera = new THREE.OrthographicCamera(-halfFrame, halfFrame, halfFrame, -halfFrame, 0.01, 100);

      try {
        composer = new EffectComposer(renderer);
        composer.setSize(RENDER_SIZE, RENDER_SIZE);
        composer.addPass(new RenderPass(scene, camera));
        gtaoPass = new GTAOPass(scene, camera, RENDER_SIZE, RENDER_SIZE);
        gtaoPass.aoRadius = 0.15; gtaoPass.distanceExponent = 1.5;
        gtaoPass.thickness = 0.05; gtaoPass.scale = preset.ao_strength;
        composer.addPass(gtaoPass);
        composer.addPass(new OutputPass());
      } catch (e) {
        console.warn("[ClientStudioRenderer] GTAOPass unavailable, rendering without AO:", e);
        composer = null; gtaoPass = null;
      }

      // ---------- Render + upload each view -----------------------------
      setPhase("rendering"); setProgress(0);
      let uploaded = 0;
      for (let i = 0; i < viewAngles.length; i++) {
        if (cancelled.current) return;
        // Pre-render context-lost check — if the GPU dropped our
        // context (iOS Safari does this on memory pressure), abort
        // this pipeline rather than uploading blank frames.
        if (contextLost || renderer.getContext()?.isContextLost?.()) {
          sendTelemetry({
            ...gpuMeta, listing_id: listingId, event: "failure",
            view_index: i, expected_views: viewAngles.length, received_views: uploaded,
            file_size_bytes: null, poly_count: polyCount, context_lost: true,
            reason: "WebGL context was lost mid-render",
          });
          throw new Error("Your browser lost the WebGL context mid-render — please upload your own photos to publish.");
        }
        const { name, elev_deg: elev, azim_deg: azim } = viewAngles[i];
        const elRad = (elev * Math.PI) / 180;
        const azRad = (azim * Math.PI) / 180;
        const dist = 3.0;
        camera.position.set(
          dist * Math.cos(elRad) * Math.sin(azRad),
          dist * Math.sin(elRad),
          dist * Math.cos(elRad) * Math.cos(azRad),
        );
        camera.up.set(0, 1, 0);
        camera.lookAt(0, 0, 0);
        camera.updateProjectionMatrix();

        try {
          if (composer) composer.render();
          else renderer.render(scene, camera);
        } catch (e) {
          sendTelemetry({
            ...gpuMeta, listing_id: listingId, event: "failure",
            view_index: i, expected_views: viewAngles.length, received_views: uploaded,
            poly_count: polyCount, reason: `render() threw: ${e?.message || e}`,
          });
          throw new Error(`Render failed on view "${name}": ${e.message || e}`);
        }

        // Second context-lost check right before we harvest pixels —
        // toBlob on a lost context returns an empty/black JPEG.
        if (contextLost || renderer.getContext()?.isContextLost?.()) {
          sendTelemetry({
            ...gpuMeta, listing_id: listingId, event: "failure",
            view_index: i, expected_views: viewAngles.length, received_views: uploaded,
            poly_count: polyCount, context_lost: true,
            reason: "context lost before toBlob",
          });
          throw new Error("Your browser lost the WebGL context mid-render — please upload your own photos to publish.");
        }

        const blob = await new Promise((resolve, reject) => {
          try {
            canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
                          "image/jpeg", 0.92);
          } catch (e) { reject(e); }
        });
        if (cancelled.current) return;
        const fd = new FormData();
        fd.append("file", blob, `${listingId}_${name}.jpg`);
        try {
          setPhase("uploading");
          await api.post(
            `/seller/listings/${listingId}/photos/upload?source=client_render&view=${encodeURIComponent(name)}`,
            fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 },
          );
        } catch (e) {
          const detail = e?.response?.data?.detail || e?.message || "upload failed";
          sendTelemetry({
            ...gpuMeta, listing_id: listingId, event: "failure",
            view_index: i, expected_views: viewAngles.length, received_views: uploaded,
            poly_count: polyCount, reason: `upload failed: ${detail}`,
          });
          throw new Error(`Uploading "${name}" failed: ${detail}`);
        }
        uploaded += 1;
        setProgress(uploaded);
        setThumbs((t) => [...t, URL.createObjectURL(blob)]);
        onProgress?.(i, name);
        setPhase("rendering");
      }

      sendTelemetry({
        ...gpuMeta, listing_id: listingId, event: "success",
        expected_views: viewAngles.length, received_views: uploaded, poly_count: polyCount,
      });
      setPhase("done");
      onComplete?.(uploaded);
    } finally {
      // ---------- Cleanup — ALWAYS runs, even on cancel / error ---------
      // Order: geometry → material → env texture → gtao render targets
      // → composer → pmrem → renderer → forceContextLoss so the WebGL
      // context is genuinely released. Mid-flight cancels used to leak
      // the context, which pushed the browser's context budget over
      // and locked out the NEXT listing's render.
      try { canvas.removeEventListener("webglcontextlost", onLost); } catch (_) {}
      try { geometry?.dispose?.(); } catch (_) {}
      try { material?.dispose?.(); } catch (_) {}
      try { envTex?.dispose?.(); } catch (_) {}
      try {
        // GTAOPass owns two render targets (`gtaoRenderTarget` + `pdRenderTarget`)
        // + several materials — dispose all of them so the WebGL memory
        // is actually reclaimed.
        gtaoPass?.gtaoRenderTarget?.dispose?.();
        gtaoPass?.pdRenderTarget?.dispose?.();
        gtaoPass?.gtaoMaterial?.dispose?.();
        gtaoPass?.pdMaterial?.dispose?.();
        gtaoPass?.dispose?.();
      } catch (_) {}
      try { composer?.dispose?.(); } catch (_) {}
      try { pmrem?.dispose?.(); } catch (_) {}
      try { renderer?.dispose?.(); } catch (_) {}
      try { renderer?.forceContextLoss?.(); } catch (_) {}
    }
  }

  async function loadGeometry(gpuMeta = {}) {
    // Fetch bytes via the authenticated `api` client so the JWT rides
    // along — the render-source endpoint refuses non-owners. Prefer STL
    // (faster in-browser parse); fall back to 3DM if only that's present.
    //
    // 2026-02-28 large-file gate — a browser cannot safely parse an
    // STL/3DM north of ~150 MB. STLLoader.parse pushes the entire
    // vertex array into GPU RAM as a Float32Array, and a monster file
    // OOMs the tab or crashes Chrome. Rather than trigger a silent
    // failure the seller can't act on, we probe the file size UP FRONT
    // and route to the manual-upload path with a specific message.
    const CLIENT_RENDER_MAX_MB = 150;
    const preferStl = hasStl;
    const preferDm  = !hasStl && hasDm;
    const kind = preferStl ? "stl" : "dm";
    if (!preferStl && !preferDm) {
      throw new Error("Neither STL nor 3DM is available on this listing for browser rendering.");
    }
    // Cheap size probe first — hits the dedicated
    // `/render-source/size` endpoint that returns JSON only. If the
    // file is bigger than the browser can safely parse, we bail here
    // BEFORE spending bandwidth on a 200 MB download.
    try {
      const { data: sz } = await api.get(
        `/seller/listings/${listingId}/render-source/size?kind=${kind}`,
        { timeout: 15000 },
      );
      if (sz && sz.size_mb && sz.size_mb > CLIENT_RENDER_MAX_MB) {
        throw new Error(
          `This file is ${sz.size_mb} MB — too large for your browser to render safely (limit ${CLIENT_RENDER_MAX_MB} MB). Please upload your own render images to publish.`,
        );
      }
    } catch (e) {
      // If the probe failed for a reason OTHER than our own too-large
      // rejection, log and continue — the full download below still
      // enforces the ceiling via `buf.byteLength`.
      if (String(e?.message || "").includes("too large for your browser")) throw e;
      // eslint-disable-next-line no-console
      console.info("[ClientStudioRenderer] size probe skipped:", e?.message);
    }
    let buf;
    try {
      const { data } = await api.get(
        `/seller/listings/${listingId}/render-source?kind=${kind}`,
        { responseType: "arraybuffer", timeout: 180000 },
      );
      buf = data;
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "unknown error";
      throw new Error(`Could not download ${kind.toUpperCase()} for browser render: ${detail}`);
    }
    // Post-download size gate — even if the HEAD probe succeeded, if
    // the buffer itself is over the ceiling (server didn't send
    // Content-Range) we bail before parse() blows the GPU.
    if (buf && buf.byteLength > CLIENT_RENDER_MAX_MB * 1024 * 1024) {
      throw new Error(
        `This file is ${Math.round(buf.byteLength / (1024 * 1024))} MB — too large for your browser to render safely (limit ${CLIENT_RENDER_MAX_MB} MB). Please upload your own render images to publish.`,
      );
    }
    if (preferStl) {
      try {
        const loader = new STLLoader();
        return loader.parse(buf);
      } catch (e) {
        throw new Error(`STL parse failed: ${e?.message || e}`);
      }
    }
    const loader = new Rhino3dmLoader();
    loader.setLibraryPath(RHINO_LIB);
    return await new Promise((resolve, reject) => {
      try {
        loader.parse(buf, (obj) => {
          let merged = null;
          obj.traverse((c) => {
            if (c.isMesh && !merged) merged = c.geometry.clone();
          });
          if (!merged) reject(new Error("3DM had no renderable mesh."));
          else resolve(merged);
        }, (err) => reject(new Error(`3DM parse failed: ${err?.message || err}`)));
      } catch (e) { reject(e); }
    });
  }

  // ------------ UI ------------
  const TOTAL_VIEWS = FALLBACK_PRESET.view_angles.length;
  const pct = Math.round((progress / TOTAL_VIEWS) * 100);
  return (
    <div
      className="rounded-md border border-primary/30 bg-primary/5 p-4"
      data-testid="client-studio-renderer"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {phase === "done" ? (
            <Check className="h-4 w-4 text-emerald-600" />
          ) : phase === "failed" ? (
            <AlertTriangle className="h-4 w-4 text-red-600" />
          ) : (
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          )}
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-primary">
              {phase === "done"
                ? "Studio renders ready"
                : phase === "failed"
                ? "Client renderer failed"
                : phase === "preparing"
                ? "Preparing browser render"
                : phase === "rendering"
                ? "Rendering studio views in your browser"
                : "Uploading studio views"}
            </p>
            <p className="text-sm mt-1" data-testid="client-render-status">
              {phase === "failed" ? (
                <>
                  <span className="text-red-700">{errorMsg}</span>{" "}
                  <span className="text-muted-foreground">— use the manual upload below to add your own photos.</span>
                </>
              ) : (
                <>
                  <span className="font-semibold">{progress}</span>
                  <span className="text-muted-foreground"> of {TOTAL_VIEWS} views</span>
                  {" · "}
                  <span className="text-muted-foreground text-xs">
                    Runs entirely in your browser — nothing to wait for on our side.
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
        {phase === "failed" && (
          <div className="inline-flex items-center gap-1 rounded px-2 py-1 border border-amber-500/60 bg-amber-500/10 text-amber-800 text-[11px] font-semibold">
            <UploadCloud className="h-3 w-3" /> Please upload your own photos
          </div>
        )}
      </div>

      {phase !== "failed" && (
        <div className="mt-3 h-1.5 w-full rounded-full bg-primary/10 overflow-hidden" data-testid="client-render-progress">
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}

      {phase !== "failed" && phase !== "done" && (
        <div className="mt-3 grid grid-cols-4 gap-1.5">
          {(FALLBACK_PRESET.view_angles).map((v, i) => (
            <div
              key={v.name}
              data-testid={`client-render-tile-${i}`}
              className={`aspect-square rounded overflow-hidden flex items-center justify-center text-[10px] font-mono uppercase tracking-wider transition-colors ${
                thumbs[i]
                  ? "border border-emerald-500/40 bg-black"
                  : i === progress
                  ? "bg-primary/20 text-primary border border-primary/40 animate-pulse"
                  : "bg-secondary text-muted-foreground border border-border/60"
              }`}
            >
              {thumbs[i] ? (
                <img
                  src={thumbs[i]}
                  alt={v.name}
                  className="w-full h-full object-cover"
                  data-testid={`client-render-thumb-${i}`}
                />
              ) : i === progress ? (
                <Camera className="h-3 w-3" />
              ) : (
                v.name.replace("three_quarter_", "3Q ").slice(0, 5)
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Named export for the constants so a comparison harness or tests can
// verify the frontend fallback stays in sync with the server defaults.
export { FALLBACK_PRESET };
